// components/forms/Step1Form.tsx
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import SelectedLocation from "@/components/ui/selected-location";
import SelectedLocationUganda from "@/components/ui/selected-location-uganda";

import { useToast } from "@/hooks/use-toast";
import { useLiveFormData } from "@/hooks/useLiveFormData";
import { locationData } from "@/lib/location-data";
import { useRwandaLocations } from "@/lib/locationHelper";
import {
  useValuationFormStore,
  ValuationFormValues,
} from "@/store/valuationFormStore";
import { MapPin } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import ug from "ug-locations";
import { useCurrency } from "@/context/CurrencyContext";

const Step1Form: React.FC = () => {
  const { toast } = useToast();
  const { setCurrency } = useCurrency();
  const {
    register,
    formState: { errors },
    setValue,
    watch,
    unregister,
  } = useFormContext<ValuationFormValues>();

  const [selectedCountry, setSelectedCountry] = useState<"RW" | "UG">("UG");

  const { getProvinces, getDistricts, getSectors, getCells, getVillages } =
    useRwandaLocations(locationData as any);

  const { combinedData: newData } = useLiveFormData();
  const combinedData = {
    ...newData,
    plotNumber: newData?.upi?.slice(-4) || "",
  };

  console.log("Combined form data in Step 1:", combinedData);

  // Flag to block cascade effects during initialization
  const isInitializing = useRef(false);
  const initializedFromData = useRef(false);

  // Watch form values
  const selectedProvince = watch("province");
  const selectedDistrict = watch("district");
  const selectedSector = watch("sector");
  const selectedCell = watch("cell");
  const selectedVillage = watch("village");
  const address = watch("streetAddress");
  const latitude = watch("latitude");
  const longitude = watch("longitude");

  // Uganda location state
  const [ugandaRegions] = useState<string[]>([
    "Central Region",
    "Eastern Region",
    "Northern Region",
    "Western Region",
  ]);
  const [selectedUgandaRegion, setSelectedUgandaRegion] = useState<string>("");
  const [subRegions, setSubRegions] = useState<string[]>([]);
  const [selectedSubRegion, setSelectedSubRegion] = useState<string>("");
  const [ugandaDistricts, setUgandaDistricts] = useState<string[]>([]);
  const [selectedUgandaDistrict, setSelectedUgandaDistrict] =
    useState<string>("");
  const [counties, setCounties] = useState<string[]>([]);
  const [selectedCounty, setSelectedCounty] = useState<string>("");
  const [subcounties, setSubcounties] = useState<string[]>([]);
  const [selectedSubcounty, setSelectedSubcounty] = useState<string>("");
  const [parishes, setParishes] = useState<string[]>([]);
  const [selectedParish, setSelectedParish] = useState<string>("");
  const [ugandaVillages, setUgandaVillages] = useState<string[]>([]);

  const getSubRegionsByRegion = (region: string): string[] => {
    switch (region) {
      case "Northern Region":
        return ["Karamoja", "Acholi", "Lango", "West Nile"];
      case "Eastern Region":
        return ["Busoga", "Teso", "Bugisu", "Sebei"];
      case "Western Region":
        return ["Bunyoro", "Tooro", "Ankole", "Kigezi"];
      case "Central Region":
        return ["Buganda", "Kampala", "Mengo"];
      default:
        return [];
    }
  };

  const getCountiesByDistrict = (district: string): string[] => {
    const countyMap: Record<string, string[]> = {
      // A
      "Abim":           ["Labwor County"],
      "Adjumani":       ["East Moyo County"],
      "Agago":          ["Agago County"],
      "Alebtong":       ["Ajuri County", "Moroto County"],
      "Amolatar":       ["Kioga County"],
      "Amudat":         ["Upe County"],
      "Amuria":         ["Amuria County", "Kapelebyong County"],
      "Amuru":          ["Kilak County"],
      "Apac":           ["Kwania County", "Maruzi County"],
      "Arua":           ["Arua Municipal Council", "Ayivu County", "Madi-Okollo County", "Terego County", "Vurra County", "VURRA"],

      // B
      "Budaka":         ["Budaka County", "Iki-Iki County"],
      "Bududa":         ["Manjiya County"],
      "Bugiri":         ["Bukooli County"],
      "Bugweri":        ["Bugweri County"],
      "Buhweju":        ["Buhweju County"],
      "Buikwe":         ["Buikwe County"],
      "Bukedea":        ["Bukedea County"],
      "Bukomansimbi":   ["Bukomansimbi County"],
      "Bukwo":          ["Kongasis County"],
      "Bulambuli":      ["Bulambuli County"],
      "Buliisa":        ["Buliisa County"],
      "Bundibugyo":     ["Bughendera County", "Bwamba County"],
      "Bushenyi":       ["Bushenyi-Ishaka Municipal Council", "Igara County"],
      "Busia":          ["Busia Municipal Council", "Samia-Bugwe County"],
      "Butaleja":       ["Bunyole County"],
      "Butambala":      ["Butambala County"],
      "Butebo":         ["Butebo County"],
      "Buvuma":         ["Buvuma Islands County"],
      "Buyende":        ["Budiope East County", "Budiope West County"],

      // D
      "Dokolo":         ["Dokolo County"],

      // G
      "Gomba":          ["Gomba County"],
      "Gulu":           ["Aswa County", "Gulu Municipal Council", "Omoro County"],

      // H
      "Hoima":          ["Bugahya County", "Buhaguzi County", "Hoima Municipal Council"],

      // I
      "Ibanda":         ["Ibanda County"],
      "Iganga":         ["Bugweri County", "Iganga Municipal Council", "Kigulu County"],
      "Isingiro":       ["Bukanga County", "Isingiro County"],

      // J
      "Jinja":          ["Butembe County", "Jinja Municipal Council", "Kagoma County"],

      // K
      "Kaabong":        ["Dodoth East County", "Dodoth West County", "Ik County"],
      "Kabale":         ["Kabale Municipal Council", "Ndorwa County", "Rubanda County", "Rukiga County"],
      "Kabarole":       ["Bunyangabu County", "Burahya County", "Fort Portal Municipal Council", "Nakasongola County"],
      "Kaberamaido":    ["Kaberamaido County", "Kalaki County"],
      "Kalangala":      ["Bujumba County", "Kyamuswa County"],
      "Kaliro":         ["Bulamogi County"],
      "Kalungu":        ["Kalungu County"],
      "Kampala":        ["Kampala Capital City"],
      "Kamuli":         ["Bugabula County", "Buzaaya County"],
      "Kamwenge":       ["Kibale County", "Kitagwenda County"],
      "Kanungu":        ["Kinkiizi County"],
      "Kapchorwa":      ["Tingey County"],
      "Kasese":         ["Bukonzo County", "Busongora County", "Kasese Municipal Council"],
      "Katakwi":        ["Toroma County", "Usuk County"],
      "Kayunga":        ["Bbaale County", "Ntenjeru County"],
      "Kibaale":        ["Bugangaizi County", "Bugangaizi East County", "Buyaga County", "Buyaga West County", "Buyanja County"],
      "Kiboga":         ["Kiboga East County"],
      "Kibuku":         ["Kibuku County"],
      "Kiruhura":       ["Kazo County", "Nyabushozi County"],
      "Kiryandongo":    ["Kibanda County"],
      "Kisoro":         ["Bufumbira County East", "Bufumbira County North", "Bufumbira County South"],
      "Kitgum":         ["Chua County"],
      "Koboko":         ["Koboko County"],
      "Kole":           ["Kole County"],
      "Kotido":         ["Jie County"],
      "Kumi":           ["Kumi County"],
      "Kween":          ["Kween County"],
      "Kyankwanzi":     ["Kiboga West County"],
      "Kyegegwa":       ["Kyaka County"],
      "Kyenjojo":       ["Mwenge County"],

      // L
      "Lamwo":          ["Lamwo County"],
      "Lira":           ["Erute County", "Lira Municipal Council"],
      "Luuka":          ["Luuka County"],
      "Luweero":        ["Bamunanika County", "Katikamu County"],
      "Lwengo":         ["Bukoto County"],
      "Lyantonde":      ["Kabula County"],

      // M
      "Manafwa":        ["Bubulo County"],
      "Maracha":        ["Maracha County"],
      "Masaka":         ["Bukoto Central County", "Bukoto East County", "Masaka Municipal Council"],
      "Masindi":        ["Bujenje County", "Buruuli County", "Masindi Municipal Council"],
      "Mayuge":         ["Bunya County"],
      "Mbale":          ["Bungokho County", "Mbale Municipal Council"],
      "Mbarara":        ["Kashari County", "Mbarara Municipal Council", "Rwampara County"],
      "Mitooma":        ["Ruhinda County"],
      "Mityana":        ["Busujju County", "Mityana County"],
      "Moroto":         ["Matheniko County", "Moroto Municipal Council"],
      "Moyo":           ["Obongi County", "West Moyo County"],
      "Mpigi":          ["Mawokota County"],
      "Mubende":        ["Buwekula County", "Kasambya County", "Kassanda County"],
      "Mukono":         ["Mukono County", "Mukono Municipal Council", "Nakifuma Council"],

      // N
      "Nakapiripirit":  ["Chekwii (Kadam) County", "Pian County"],
      "Nakaseke":       ["Nakaseke North County", "Nakaseke South County"],
      "Nakasongola":    ["Budyebo County", "Nakasongola County"],
      "Namayingo":      ["Bukooli Island County", "Bukooli South County"],
      "Namutumba":      ["Busiki County"],
      "Napak":          ["Bokora County"],
      "Nebbi":          ["Jonam County", "Padyere County"],
      "Ngora":          ["Ngora County"],
      "Ntoroko":        ["Ntoroko County"],
      "Ntungamo":       ["Kajara County", "Ntungamo Municipal Council", "Ruhaama County", "Rushenyi County"],
      "Nwoya":          ["Nwoya County"],

      // O
      "Otuke":          ["Otuke County"],
      "Oyam":           ["Oyam County"],

      // P
      "Pader":          ["Aruu County"],
      "Pallisa":        ["Agule County", "Butebo County", "Pallisa County"],

      // R
      "Rakai":          ["Kakuuto County", "Kooki County", "Kyotera County"],
      "Rubirizi":       ["Bunyaruguru County", "Katerera County"],
      "Rukungiri":      ["Rubabo County", "Rujumbura County", "Rukungiri Municipal Council"],

      // S
      "Sembabule":      ["Lwemiyaga County", "Mawogola County"],
      "Serere":         ["Kasilo County", "Serere County"],
      "Sheema":         ["Sheema County"],
      "Sironko":        ["Budadiri County"],
      "Soroti":         ["Soroti County", "Soroti Municipal Council"],

      // T
      "Tororo":         ["Tororo County", "Tororo Municipal Council", "West Budama County"],

      // W
      "Wakiso":         ["Busiro County", "Entebbe Municipal Council", "Kyadondo County"],

      // Y
      "Yumbe":          ["Aringa County"],

      // Z
      "Zombo":          ["Okoro County"],
    };

    // ✅ Case-insensitive match — handles "ARUA", "arua", "Arua" all correctly
    const districtLower = district.toLowerCase();
    for (const [key, value] of Object.entries(countyMap)) {
      if (districtLower.includes(key.toLowerCase())) {
        return value;
      }
    }
    return ["County North", "County South", "County East", "County West"];
  };

  // Initialize currency on mount
  useEffect(() => {
    const country = watch("country");
    if (!country || country === "RW") {
      setCurrency("RWF");
    } else if (country === "UG") {
      setCurrency("UGX");
    }
  }, []);

  // Load all Uganda districts on mount
  useEffect(() => {
    const districts: string[] = ug.getDistricts();
    setUgandaDistricts(districts.sort());
  }, []);

  // ─── MAIN INITIALIZATION EFFECT ───────────────────────────────────────────
  useEffect(() => {
    if (!combinedData?.id) return;
    if (initializedFromData.current) return;
    if (!combinedData?.country) return;

    console.log("Initializing form with combined data:", combinedData);

    const country = combinedData.country as "RW" | "UG";

    if (country === "RW") {
      setSelectedCountry("RW");
      setCurrency("RWF");
      setValue("country", "RW");
      initializedFromData.current = true;
      return;
    }

    if (country === "UG") {
      isInitializing.current = true;
      initializedFromData.current = true;

      setSelectedCountry("UG");
      setCurrency("UGX");
      setValue("country", "UG");

      const region = combinedData.region || "";
      const subRegion = combinedData.subRegion || "";
      const district = combinedData.district || "";
      const county = combinedData.county || "";
      const subcounty = combinedData.subcounty || "";
      const parish = combinedData.parish || "";
      const village = combinedData.village || "";

      // Region — set options list first, then selected value
      if (region) {
        const regionSubRegions = getSubRegionsByRegion(region);
        setSubRegions(regionSubRegions);
        setSelectedUgandaRegion(region);
        setValue("region", region);
      }

      // Sub-region
      if (subRegion) {
        setSelectedSubRegion(subRegion);
        setValue("subRegion", subRegion);
      }

      // District — set county options list first, then selected district
      if (district) {
        const countyList = getCountiesByDistrict(district); // case-insensitive now
        setCounties(countyList);
        setSelectedUgandaDistrict(district);
        setValue("district", district);

        try {
          const subs: string[] = ug.getSubcountiesInDistrict(district);
          setSubcounties(subs);
        } catch {
          setSubcounties([]);
        }
      }

      // County — options already set above, now set value
      if (county) {
        setSelectedCounty(county);
        setValue("county", county);
      }

      // Subcounty
      if (subcounty && district) {
        setSelectedSubcounty(subcounty);
        setValue("sector", subcounty);

        try {
          const parishList: string[] = ug.getParishesInSubcounty(district, subcounty);
          setParishes(parishList);
        } catch {
          setParishes([]);
        }
      }

      // Parish
      if (parish && district && subcounty) {
        setSelectedParish(parish);
        setValue("cell", parish);

        try {
          const villageList: string[] = ug.getVillagesInParish(district, subcounty, parish);
          setUgandaVillages(villageList);
        } catch {
          setUgandaVillages([]);
        }
      }

      // Village
      if (village) {
        setValue("village", village);
      }

      // Release cascade lock after all React state updates have committed
      setTimeout(() => {
        isInitializing.current = false;
      }, 1000);
    }
  }, [combinedData?.id, combinedData?.region]);

  // ─── CASCADE EFFECTS (blocked during initialization) ──────────────────────

  useEffect(() => {
    if (isInitializing.current) return;
    if (selectedUgandaRegion) {
      setValue("region", selectedUgandaRegion);
      const regionSubRegions = getSubRegionsByRegion(selectedUgandaRegion);
      setSubRegions(regionSubRegions);

      setSelectedSubRegion("");
      setSelectedUgandaDistrict("");
      setSelectedCounty("");
      setSelectedSubcounty("");
      setSelectedParish("");
      setUgandaVillages([]);
      setCounties([]);
      setSubcounties([]);
      setParishes([]);
    }
  }, [selectedUgandaRegion]);

  useEffect(() => {
    if (isInitializing.current) return;
    if (selectedSubRegion) {
      setValue("subRegion", selectedSubRegion);
    }
  }, [selectedSubRegion]);

  useEffect(() => {
    if (isInitializing.current) return;
    if (selectedUgandaDistrict) {
      const districtCounties = getCountiesByDistrict(selectedUgandaDistrict);
      setCounties(districtCounties);
      setValue("district", selectedUgandaDistrict);

      setSelectedCounty("");
      setSelectedSubcounty("");
      setParishes([]);
      setSelectedParish("");
      setUgandaVillages([]);
    }
  }, [selectedUgandaDistrict]);

  useEffect(() => {
    if (isInitializing.current) return;
    if (selectedCounty) {
      setValue("county", selectedCounty);
      if (selectedUgandaDistrict) {
        const subs = ug.getSubcountiesInDistrict(selectedUgandaDistrict);
        setSubcounties(subs);
      }
    }
  }, [selectedCounty, selectedUgandaDistrict]);

  useEffect(() => {
    if (isInitializing.current) return;
    if (selectedUgandaDistrict && selectedSubcounty) {
      const parishList = ug.getParishesInSubcounty(
        selectedUgandaDistrict,
        selectedSubcounty,
      );
      setParishes(parishList);
      setValue("sector", selectedSubcounty);
      setSelectedParish("");
      setUgandaVillages([]);
    }
  }, [selectedUgandaDistrict, selectedSubcounty]);

  useEffect(() => {
    if (isInitializing.current) return;
    if (selectedUgandaDistrict && selectedSubcounty && selectedParish) {
      const villageList = ug.getVillagesInParish(
        selectedUgandaDistrict,
        selectedSubcounty,
        selectedParish,
      );
      setUgandaVillages(villageList);
      setValue("cell", selectedParish);
    }
  }, [selectedUgandaDistrict, selectedSubcounty, selectedParish]);

  const handleVillageSelect = (village: string) => {
    setValue("village", village);
  };

  const handleCountryChange = (country: "RW" | "UG") => {
    setSelectedCountry(country);
    setValue("country", country);

    if (country === "RW") {
      setCurrency("RWF");
    } else {
      setCurrency("UGX");
    }

    setValue("province", "");
    setValue("district", "");
    setValue("sector", "");
    setValue("cell", "");
    setValue("village", "");
    setValue("region", "");
    setValue("subRegion", "");
    setValue("county", "");
    setValue("subcounty", "");
    setValue("parish", "");

    setSelectedUgandaRegion("");
    setSelectedSubRegion("");
    setSelectedUgandaDistrict("");
    setSelectedCounty("");
    setSelectedSubcounty("");
    setSelectedParish("");
    setSubRegions([]);
    setCounties([]);
    setSubcounties([]);
    setParishes([]);
    setUgandaVillages([]);
  };

  useEffect(() => {
    const country = watch("country");
    if (country) {
      if (country === "RW") {
        setCurrency("RWF");
        setSelectedCountry("RW");
      } else if (country === "UG") {
        setCurrency("UGX");
        setSelectedCountry("UG");
      }
    }
  }, [watch("country")]);

  // Rwanda locations
  const provinces = selectedCountry === "RW" ? getProvinces() : [];
  const districts =
    selectedCountry === "RW" && selectedProvince
      ? getDistricts(selectedProvince)
      : [];
  const sectors =
    selectedCountry === "RW" && selectedProvince && selectedDistrict
      ? getSectors(selectedProvince, selectedDistrict)
      : [];
  const cells =
    selectedCountry === "RW" &&
    selectedProvince &&
    selectedDistrict &&
    selectedSector
      ? getCells(selectedProvince, selectedDistrict, selectedSector)
      : [];
  const villages =
    selectedCountry === "RW" &&
    selectedProvince &&
    selectedDistrict &&
    selectedSector &&
    selectedCell
      ? getVillages(
          selectedProvince,
          selectedDistrict,
          selectedSector,
          selectedCell,
        )
      : [];

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Your browser does not support GPS.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setValue("latitude", latitude);
        setValue("longitude", longitude);
      },
      (error) => {
        alert("Failed to get location: " + error.message);
      },
    );
  };

  return (
    <div className="space-y-6 p-6">
      {/* Basic Property Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Basic Property Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              UPI *
            </label>
            <input
              type="text"
              {...register("upi", { required: "UPI is required" })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter UPI"
            />
            {errors.upi && (
              <p className="mt-1 text-sm text-red-600">{errors.upi.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Plot Number *
            </label>
            <input
              type="text"
              {...register("plotNumber", {
                required: "Plot number is required",
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter plot number"
            />
            {errors.plotNumber && (
              <p className="mt-1 text-sm text-red-600">
                {errors.plotNumber.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Owner ID *
            </label>
            <input
              type="text"
              {...register("ownerIdNumber", {
                required: "Owner ID is required",
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter owner ID"
            />
            {errors.ownerIdNumber && (
              <p className="mt-1 text-sm text-red-600">
                {errors.ownerIdNumber.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Owner Name *
            </label>
            <input
              type="text"
              {...register("ownerName", { required: "Owner name is required" })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter owner name"
            />
            {errors.ownerName && (
              <p className="mt-1 text-sm text-red-600">
                {errors.ownerName.message}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Location Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Location</h3>

        {/* Country Selection */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Country *
          </label>
          <div className="flex space-x-4">
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                value="RW"
                {...register("country", { required: "Country is required" })}
                checked={selectedCountry === "RW"}
                onChange={(e) => {
                  register("country").onChange(e);
                  handleCountryChange("RW");
                }}
                className="w-4 h-4 text-blue-600"
              />
              <span>Rwanda</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                value="UG"
                {...register("country", { required: "Country is required" })}
                checked={selectedCountry === "UG"}
                onChange={(e) => {
                  register("country").onChange(e);
                  handleCountryChange("UG");
                }}
                className="w-4 h-4 text-blue-600"
              />
              <span>Uganda</span>
            </label>
          </div>
          {errors.country && (
            <p className="mt-1 text-sm text-red-600">
              {errors.country.message}
            </p>
          )}
        </div>

        {selectedCountry === "RW" ? (
          // Rwanda Location Fields
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Province *
              </label>
              <select
                {...register("province", { required: "Province is required" })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Province</option>
                {provinces.map((province) => (
                  <option key={province} value={province}>
                    {province}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                District *
              </label>
              <select
                {...register("district", { required: "District is required" })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select District</option>
                {districts.map((district) => (
                  <option key={district} value={district}>
                    {district}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sector *
              </label>
              <select
                {...register("sector", { required: "Sector is required" })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Sector</option>
                {sectors.map((sector) => (
                  <option key={sector} value={sector}>
                    {sector}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cell *
              </label>
              <select
                {...register("cell", { required: "Cell is required" })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Cell</option>
                {cells.map((cell) => (
                  <option key={cell} value={cell}>
                    {cell}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Village *
              </label>
              <select
                {...register("village", { required: "Village is required" })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Village</option>
                {villages.map((village) => (
                  <option key={village} value={village}>
                    {village}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <input
                type="text"
                {...register("streetAddress")}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter address"
              />
            </div>
          </div>
        ) : (
          // Uganda Location Fields
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Administrative Region *
              </label>
              <select
                value={selectedUgandaRegion}
                onChange={(e) => setSelectedUgandaRegion(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Administrative Region</option>
                {ugandaRegions.map((region) => (
                  <option key={region} value={region}>
                    {region}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sub-region (Optional)
              </label>
              <select
                value={selectedSubRegion}
                onChange={(e) => setSelectedSubRegion(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={!selectedUgandaRegion}
              >
                <option value="">Select Sub-region</option>
                {subRegions.map((subRegion) => (
                  <option key={subRegion} value={subRegion}>
                    {subRegion}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                District *
              </label>
              <select
                value={selectedUgandaDistrict}
                onChange={(e) => setSelectedUgandaDistrict(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select District</option>
                {ugandaDistricts.map((district) => (
                  <option key={district} value={district}>
                    {district}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                County *
              </label>
              <select
                value={selectedCounty}
                onChange={(e) => setSelectedCounty(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={!selectedUgandaDistrict}
              >
                <option value="">Select County</option>
                {counties.map((county) => (
                  <option key={county} value={county}>
                    {county}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subcounty *
              </label>
              <select
                value={selectedSubcounty}
                onChange={(e) => setSelectedSubcounty(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={!selectedUgandaDistrict}
              >
                <option value="">Select Subcounty</option>
                {subcounties.map((subcounty) => (
                  <option key={subcounty} value={subcounty}>
                    {subcounty}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Parish *
              </label>
              <select
                value={selectedParish}
                onChange={(e) => setSelectedParish(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={!selectedSubcounty}
              >
                <option value="">Select Parish</option>
                {parishes.map((parish) => (
                  <option key={parish} value={parish}>
                    {parish}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Village *
              </label>
              <select
                value={watch("village")}
                onChange={(e) => handleVillageSelect(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={!selectedParish}
              >
                <option value="">Select Village</option>
                {ugandaVillages.map((village) => (
                  <option key={village} value={village}>
                    {village}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <input
                type="text"
                {...register("streetAddress")}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter address"
              />
            </div>
          </div>
        )}
      </div>

      {/* Coordinates Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Coordinates</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Latitude
            </label>
            <input
              type="number"
              {...register("latitude", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="-1.9441"
              readOnly
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Longitude
            </label>
            <input
              type="number"
              {...register("longitude", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="30.0619"
              readOnly
            />
          </div>

          <div>
            <Label className="block text-sm font-medium text-gray-700 mb-1">
              GPS Location
            </Label>
            <Button
              type="button"
              variant="outline"
              onClick={getCurrentLocation}
              className="w-full mt-1"
            >
              <MapPin className="w-4 h-4 mr-2" />
              Get GPS Location
            </Button>
          </div>
        </div>
      </div>

      {/* Selected Location Display */}
      {selectedCountry === "RW" &&
        selectedProvince &&
        selectedDistrict &&
        selectedSector &&
        selectedCell &&
        selectedVillage && (
          <SelectedLocation
            province={selectedProvince}
            district={selectedDistrict}
            sector={selectedSector}
            cell={selectedCell}
            village={selectedVillage}
            address={address}
            latitude={latitude}
            longitude={longitude}
          />
        )}

      {selectedCountry === "UG" &&
        selectedUgandaRegion &&
        selectedUgandaDistrict && (
          <SelectedLocationUganda
            region={selectedUgandaRegion}
            subRegion={selectedSubRegion}
            district={selectedUgandaDistrict}
            county={selectedCounty}
            subcounty={selectedSubcounty}
            parish={selectedParish}
            village={watch("village")}
            address={address}
            latitude={latitude}
            longitude={longitude}
          />
        )}

      {/* Valuation Parameters */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Valuation Parameters
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Land Use
            </label>
            <select
              {...register("landUse")}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select land use</option>
              <option value="Residential">Residential</option>
              <option value="Commercial">Commercial</option>
              <option value="Industrial">Industrial</option>
              <option value="Agricultural">Agricultural</option>
              <option value="Mixed">Mixed Use</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Slope Category
            </label>
            <select
              {...register("slopeCategory")}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select slope category</option>
              <option value="Flat">Flat (0-5%)</option>
              <option value="Gentle">Gentle (5-15%)</option>
              <option value="Moderate">Moderate (15-30%)</option>
              <option value="Steep">Steep (30%+)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Land Area (m²)
            </label>
            <input
              type="number"
              {...register("landArea", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter land area"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Land Rate (per m²)
            </label>
            <input
              type="number"
              {...register("landRate", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter land rate"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Main House Area (m²)
            </label>
            <input
              type="number"
              {...register("mainHouseArea", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter main house area"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Main House Rate (per m²)
            </label>
            <input
              type="number"
              {...register("mainHouseRate", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter main house rate"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Annex House Area (m²)
            </label>
            <input
              type="number"
              {...register("annexHouseArea", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter annex house area"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Annex Rate (per m²)
            </label>
            <input
              type="number"
              {...register("annexRate", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter annex rate"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fence Length (m)
            </label>
            <input
              type="number"
              {...register("fenceLength", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter fence length"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fence Rate (per m)
            </label>
            <input
              type="number"
              {...register("fenceRate", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter fence rate"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Retaining Walls Length (m)
            </label>
            <input
              type="number"
              {...register("retainingWallsLength", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter retaining walls length"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Retaining Wall Rate (per m)
            </label>
            <input
              type="number"
              {...register("retainingWallRate", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter retaining wall rate"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Entrance Gate Length (m)
            </label>
            <input
              type="number"
              {...register("entranceGateLength", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter gate length"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Entrance Gate Rate (per m)
            </label>
            <input
              type="number"
              {...register("entranceGateRate", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter gate rate"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Paved Area (m²)
            </label>
            <input
              type="number"
              {...register("pavedArea", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter paved area"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Paved Area Rate (per m²)
            </label>
            <input
              type="number"
              {...register("pavedAreaRate", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter paved area rate"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Septic Tank Number
            </label>
            <input
              type="number"
              {...register("septicTankNumber", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter septic tank number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Septic Tank Value (per unit)
            </label>
            <input
              type="number"
              {...register("septicTankValue", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter septic tank value"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Garden Area (m²)
            </label>
            <input
              type="number"
              {...register("gardenArea", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter garden area"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Garden Rate (per m²)
            </label>
            <input
              type="number"
              {...register("gardenRate", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter garden rate"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Verandah Area (m²)
            </label>
            <input
              type="number"
              {...register("verandahArea", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter verandah area"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Verandah Rate (per m²)
            </label>
            <input
              type="number"
              {...register("verandahRate", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter verandah rate"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Landscaping Value
            </label>
            <input
              type="number"
              {...register("landscapingValue", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter landscaping value"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Forced Sale Discount (%)
            </label>
            <input
              type="number"
              {...register("forcedSaleDiscount", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter forced sale discount percentage"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Depreciation Rate (%)
            </label>
            <input
              type="number"
              {...register("depreciationRate", { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter depreciation rate percentage"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step1Form;